/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CustomStyledDatePicker from '../../../Components/Datepicker/CustomStyledDatePicker';
import {
  createApprovalLinkItem,
  fromPickerDate,
  ModalShell,
  normalizeApprovalLinks,
  toISODate,
  toPickerDate,
} from './modalsShared';

export const ProgressEditModal = ({
  isOpen,
  data,
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Update Progress %',
  headerAction = null,
  submitLabel = 'Update',
  submittingLabel = 'Updating...',
}) => {
  const progressPlaceholder = title === 'Add Progress %' ? 'Add progress %' : 'Change progress %';

  const [formData, setFormData] = useState({
    date: '',
    progress: '',
    remarks: '',
  });

  useEffect(() => {
    if (!data) return;

    setFormData({
      date: toISODate(data.date),
      progress: data.progress || '',
      remarks: data.remarks || '',
    });
  }, [data]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formData, id: data?.id, row_id: data?.row_id ?? data?.id });
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="medium"
      headerAction={headerAction}
      modalClassName="edit-modal edit-progress-modal"
    >
      <form className="modal-form" onSubmit={handleSubmit}>
        <div className="form-group form-group-date">
          <div className="certification-date-container custom-project-details-date-container">
            <label className="datepicker-input-label">Date</label>
            <CustomStyledDatePicker
              label=""
              placeholder="Choose date"
              value={toPickerDate(formData.date)}
              onChange={(date) => setFormData({ ...formData, date: fromPickerDate(date) })}
              displayFormat="DD-MMM-YYYY"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="project-progress-input">Progress %</label>
          <input
            id="project-progress-input"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={formData.progress}
            onChange={(event) => setFormData({ ...formData, progress: event.target.value })}
            placeholder={progressPlaceholder}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="project-progress-remarks">Comments</label>
          <textarea
            id="project-progress-remarks"
            value={formData.remarks}
            onChange={(event) => setFormData({ ...formData, remarks: event.target.value })}
            placeholder="Enter comments"
            rows="4"
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

ProgressEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  title: PropTypes.string,
  headerAction: PropTypes.node,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};

export const StatusEditModal = ({
  isOpen,
  data,
  projectName = '',
  statusOptions,
  onClose,
  onSubmit,
  onHistory,
  isSubmitting = false,
  isLoading = false,
  isBlocked = false,
  blockedMessage = '',
}) => {
  const [formData, setFormData] = useState({
    project_status_id: '',
    effectiveDate: '',
    remarks: '',
  });
  const currentStatusId = String(data?.project_status_id ?? '');
  const isSameStatusSelected = Boolean(
    currentStatusId
    && String(formData.project_status_id || '') === currentStatusId,
  );
  const sameStatusMessage = 'Current project status and new status cannot be the same.';

  useEffect(() => {
    if (!data) return;
    setFormData({
      project_status_id: data.project_status_id !== null && data.project_status_id !== undefined
        ? String(data.project_status_id)
        : '',
      effectiveDate: toISODate(data.effectiveDate),
      remarks: data.remarks || '',
    });
  }, [data]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isLoading || isBlocked || isSameStatusSelected) return;
    onSubmit({
      project_status_id: formData.project_status_id,
      effectiveDate: formData.effectiveDate,
      remarks: formData.remarks,
    });
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="New status"
      size="medium"
      modalClassName="edit-modal edit-status-modal"
      headerAction={(
        <button
          type="button"
          className="modal-header-link"
          onClick={onHistory || (() => {})}
        >
          History
        </button>
      )}
    >
      <form className="modal-form" onSubmit={handleSubmit}>
        {projectName ? (
          <div className="status-edit-project-name">
            <span className="status-edit-project-name__label">Project</span>
            <span className="status-edit-project-name__value">{projectName}</span>
          </div>
        ) : null}
        {isBlocked && blockedMessage ? (
          <div className="request-history-blocked-message">{blockedMessage}</div>
        ) : null}
        {isLoading ? (
          <div className="request-history-blocked-message">Loading latest status details...</div>
        ) : null}
        {!isLoading && !isBlocked && isSameStatusSelected ? (
          <div className="request-history-blocked-message">{sameStatusMessage}</div>
        ) : null}

        <div className="form-group">
          <label htmlFor="project-status-select">Status*</label>
          <select
            id="project-status-select"
            value={String(formData.project_status_id || '')}
            onChange={(event) => setFormData((prev) => ({ ...prev, project_status_id: event.target.value }))}
            required
            disabled={isSubmitting || isLoading || isBlocked}
          >
            <option value="" disabled>Select status</option>
            {(Array.isArray(statusOptions) ? statusOptions : []).map((option) => (
              <option key={option.value} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group form-group-date">
          <div className="certification-date-container custom-project-details-date-container">
            <label className="datepicker-input-label">Effective date</label>
            <CustomStyledDatePicker
              label=""
              placeholder="Select effective date"
              value={toPickerDate(formData.effectiveDate)}
              onChange={(date) => setFormData({ ...formData, effectiveDate: fromPickerDate(date) })}
              displayFormat="DD-MMM-YYYY"
              isDisabled={isSubmitting || isBlocked}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="project-status-remarks">Remarks</label>
          <textarea
            id="project-status-remarks"
            value={formData.remarks}
            onChange={(event) => setFormData((prev) => ({ ...prev, remarks: event.target.value }))}
            placeholder="Enter remarks..."
            rows="4"
            disabled={isSubmitting || isLoading || isBlocked}
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || isLoading || isBlocked || isSameStatusSelected || !formData.project_status_id}
          >
            {isSubmitting ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

StatusEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    project_status_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    effectiveDate: PropTypes.string,
    remarks: PropTypes.string,
  }),
  projectName: PropTypes.string,
  statusOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired,
  })),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onHistory: PropTypes.func,
  isSubmitting: PropTypes.bool,
  isLoading: PropTypes.bool,
  isBlocked: PropTypes.bool,
  blockedMessage: PropTypes.string,
};

export const TimelineEditModal = ({
  isOpen,
  data,
  onClose,
  onSubmit,
  onHistory,
  isSubmitting = false,
}) => {
  const dateOptions = [
    { value: 'rlsd', label: 'Baseline start date' },
    { value: 'rled', label: 'Baseline end date' },
    { value: 'acsd', label: 'Actual start date' },
    { value: 'rped', label: 'Replanned end date' },
  ];

  const [formData, setFormData] = useState({
    dateType: 'rlsd',
    newDate: '',
    approvalLinks: [createApprovalLinkItem('')],
    reason: '',
  });

  const convertDateToISO = (value) => toISODate(value);

  useEffect(() => {
    if (!data) return;

    const defaultType = 'rlsd';
    const initialDate = convertDateToISO(data[defaultType]);
    setFormData((prev) => ({
      ...prev,
      dateType: defaultType,
      newDate: initialDate,
      approvalLinks: normalizeApprovalLinks(data.approvalLinks),
      reason: data.reason || '',
    }));
  }, [data]);

  const handleDateTypeChange = (event) => {
    const nextType = event.target.value;
    const nextDate = convertDateToISO(data?.[nextType]);
    setFormData((prev) => ({
      ...prev,
      dateType: nextType,
      newDate: nextDate || prev.newDate,
    }));
  };

  const handleApprovalLinkChange = (value, linkId) => {
    setFormData((prev) => ({
      ...prev,
      approvalLinks: prev.approvalLinks.map((link) => (
        link.id === linkId ? { ...link, value } : link
      )),
    }));
  };

  const handleAddApprovalLink = () => {
    setFormData((prev) => ({
      ...prev,
      approvalLinks: [...prev.approvalLinks, createApprovalLinkItem('')],
    }));
  };

  const handleRemoveApprovalLink = (linkId) => {
    setFormData((prev) => ({
      ...prev,
      approvalLinks: prev.approvalLinks.filter((link) => link.id !== linkId),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...formData,
      approvalLinks: formData.approvalLinks.map((link) => link.value),
      id: data?.id,
    });
  };

  if (!isOpen) return null;
  const firstApprovalLinkInputId = `timeline-approval-link-${formData.approvalLinks[0]?.id || 'first'}`;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Edit timeline"
      size="large"
      modalClassName="edit-modal edit-timeline-modal"
      headerAction={(
        <button
          type="button"
          className="modal-header-link"
          onClick={onHistory}
        >
          History
        </button>
      )}
    >
      <form className="modal-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="timeline-date-type">Date type</label>
          <select id="timeline-date-type" value={formData.dateType} onChange={handleDateTypeChange} required>
            {dateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group form-group-date">
          <div className="certification-date-container custom-project-details-date-container">
            <label className="datepicker-input-label">New date</label>
            <CustomStyledDatePicker
              label=""
              placeholder="Choose date"
              value={toPickerDate(formData.newDate)}
              onChange={(date) => setFormData({ ...formData, newDate: fromPickerDate(date) })}
              displayFormat="DD-MMM-YYYY"
            />
          </div>
        </div>

        <div className="form-group">
          <div className="label-row">
            <label htmlFor={firstApprovalLinkInputId}>Approval link</label>
            <button
              type="button"
              className="approval-link-btn"
              onClick={handleAddApprovalLink}
              aria-label="Add approval link"
            >
              +
            </button>
          </div>
          <div className="approval-links">
            {formData.approvalLinks.map((link, index) => (
              <div key={link.id} className="approval-link-row">
                <input
                  id={index === 0 ? firstApprovalLinkInputId : `timeline-approval-link-${link.id}`}
                  className="approval-link-input"
                  type="text"
                  value={link.value}
                  onChange={(event) => handleApprovalLinkChange(event.target.value, link.id)}
                  placeholder={`Enter approval link ${index + 1}`}
                />
                {formData.approvalLinks.length > 1 ? (
                  <div className="approval-link-actions">
                    <button
                      type="button"
                      className="approval-link-btn"
                      onClick={() => handleRemoveApprovalLink(link.id)}
                      aria-label="Remove approval link"
                    >
                      -
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="timeline-reason">Reason for change</label>
          <textarea
            id="timeline-reason"
            value={formData.reason}
            onChange={(event) => setFormData({ ...formData, reason: event.target.value })}
            placeholder="Enter reason for change..."
            rows="4"
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

TimelineEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onHistory: PropTypes.func,
  isSubmitting: PropTypes.bool,
};
